import { SaveLocationsDocument } from '~/client/graph/operations/generated/Locations.generated'
import type {
  IDeliveryHierarchyParentInput,
  ILocationBase,
  ILocationInput,
} from '~/client/graph/types.generated'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import {
  compactResourceData,
  mergeResourceData,
  splitResourceFormData,
} from '~/client/src/shared/hooks/splitResourceFormData'

import { isLocationResourceType, LOCATION_KIND_PROPERTY } from './locationKind'
import {
  parseLocationResource,
  toLocationResourceRecord,
  type LocationResource,
  type MissingLocationResourceId,
} from './locationResource'
import type {
  ProjectResourceFailure,
  ProjectResourceType,
  ResourceSaveCommand,
} from './projectResourceType'
import type { ResourceResult } from './resourceResult'
import { resourceErr, resourceOk } from './resourceResult'

const LOCATION_DEFAULTS: Pick<ILocationInput, 'color' | 'iconName'> = {
  color: '#808080',
  iconName: 'map-marker',
}

export interface MissingLocationResourceRecord {
  readonly _tag: 'MissingLocationResourceRecord'
  readonly recordId: string
}

export type LocationResourceDomainFailure =
  | MissingLocationResourceId
  | MissingLocationResourceRecord

export type LocationResourceFailure =
  ProjectResourceFailure<LocationResourceDomainFailure>

/**
 * Locations are the rows of the shared collection that are a place.
 *
 * Membership is decided by the RESOURCE type, never by whether a kind could be read.
 * Those are different questions, and conflating them means an unclassified location -
 * one created without picking a Type, or one whose Type was cleared - silently vanishes
 * from the list instead of showing an empty Type cell the user can fix. A location with
 * no kind is still a location; it is a location that needs one (STX1-597).
 */
export function decodeLocationResources(
  locations: readonly ILocationBase[],
): ResourceResult<readonly LocationResource[], LocationResourceDomainFailure> {
  const resources: LocationResource[] = []

  for (const location of locations) {
    if (location.isDeleted) continue
    if (!isLocationResourceType(location.type)) continue

    const resource = parseLocationResource(location)
    if (resource._tag === 'Err') return resource

    resources.push(resource.value)
  }

  return resourceOk(resources)
}

/**
 * Normalizes a form or record parent into the Location mutation shape.
 *
 * Unlike the Equipment normalizer this does NOT discard a ProjectLocation parent. For
 * equipment the project root means "not parented to anything"; for a location it is the
 * site itself: the map setup (MapViewItemsSetup.store) parents every newly drawn object to
 * it, and LocationBase defaults a parentless record to it. Dropping it would silently
 * unparent those objects.
 */
export function toLocationParentInput(
  parent: unknown,
): IDeliveryHierarchyParentInput | null {
  if (!parent || typeof parent !== 'object') return null

  const parentId = 'parentId' in parent ? parent.parentId : null

  if (parentId === null || parentId === undefined || parentId === '') {
    return null
  }

  // The parent is named by id alone. Any parentType the form or record still carries is
  // dropped: Graph records the parent's kind itself on save (STX1-1355).
  return { parentId: String(parentId) }
}

const locationInputOf = (
  resource: LocationResource,
  projectId: string,
  recordId: string,
  rootFields: Readonly<Record<string, unknown>>,
  dataFields: Readonly<Record<string, unknown>>,
  templateId: string | null | undefined,
): ILocationInput => ({
  ...resource.kindFields,
  // A vertical object's accessible levels are edited on the map, which sends them along.
  ...(Array.isArray(rootFields.accessibleLevels) && {
    accessibleLevels: rootFields.accessibleLevels,
  }),
  assignedGlobes: [...resource.assignedGlobes],
  assignedSitemaps: [...resource.assignedSitemaps],
  code: resource.code,
  // iconName, color and parent are root fields the Location form edits, so the submitted
  // value has to win over the stored one. Sending the record's own values back made every
  // icon, colour and parent edit look saved and silently discard itself.
  color:
    typeof rootFields.color === 'string' ? rootFields.color : resource.color,
  // `data` is $set whole on the server, so a partial send drops the keys it omits -
  // including the kind. Merge onto the resource's own data and re-assert the kind rather
  // than trusting the form to carry it.
  data: withKind(
    mergeResourceData({ ...resource.data }, { ...dataFields }),
    resource.kind,
  ),
  iconName:
    typeof rootFields.iconName === 'string'
      ? rootFields.iconName
      : resource.iconName,
  id: recordId,
  maxBookingDuration: resource.maxBookingDuration,
  maxOverlappingDeliveries: resource.maxOverlappingDeliveries,
  name: typeof rootFields.name === 'string' ? rootFields.name : resource.name,
  operatingIntervals: Array.isArray(rootFields.operatingIntervals)
    ? rootFields.operatingIntervals.map(
        ({ startDate, endDate, isDaily, dayOfWeek }) => ({
          startDate,
          endDate,
          isDaily,
          dayOfWeek,
        }),
      )
    : resource.operatingIntervals.map(interval => ({ ...interval })),
  // Only an edit that actually carried a parent overrides the stored one; a form that
  // never showed the field must not clear it.
  parent:
    'parent' in rootFields
      ? toLocationParentInput(rootFields.parent)
      : toLocationParentInput(resource.parent),
  permittedCompanies: Array.isArray(rootFields.permittedCompanies)
    ? rootFields.permittedCompanies
    : [...resource.permittedCompanies],
  projectId,
  templateId: templateId ?? resource.templateId,
})

function withKind(
  data: Record<string, unknown>,
  kind: string | null,
): Record<string, unknown> {
  if (data[LOCATION_KIND_PROPERTY] || !kind) return data
  return { ...data, [LOCATION_KIND_PROPERTY]: kind }
}

const LOCATION_ROOT_EXTRAS = [
  'operatingIntervals',
  'permittedCompanies',
  'accessibleLevels',
]

export function buildNewLocationInput(
  projectId: string,
  formData: Readonly<Record<string, unknown>>,
  id?: string,
): ILocationInput {
  const { templateId, rootFields, dataFields } = splitResourceFormData(
    ResourceTypeId.LOCATIONS,
    { ...formData },
    LOCATION_ROOT_EXTRAS,
  )

  return {
    id,
    projectId,
    name: typeof rootFields.name === 'string' ? rootFields.name.trim() : '',
    color:
      typeof rootFields.color === 'string'
        ? rootFields.color
        : LOCATION_DEFAULTS.color,
    iconName:
      typeof rootFields.iconName === 'string'
        ? rootFields.iconName
        : LOCATION_DEFAULTS.iconName,
    parent: toLocationParentInput(rootFields.parent),
    templateId: templateId ?? null,
    data: compactResourceData({ ...dataFields }),
  }
}

export function buildLocationSaveVariables(
  projectId: string,
  command: ResourceSaveCommand,
  resources: readonly LocationResource[],
): ResourceResult<
  { readonly locations: readonly ILocationInput[] },
  LocationResourceDomainFailure
> {
  const { recordId, formData } = command

  if (recordId === 'new' || !recordId) {
    return resourceOk({
      locations: [buildNewLocationInput(projectId, formData)],
    })
  }

  const existing = resources.find(resource => resource.id === recordId)
  if (!existing) {
    return resourceErr({ _tag: 'MissingLocationResourceRecord', recordId })
  }

  const { templateId, rootFields, dataFields } = splitResourceFormData(
    ResourceTypeId.LOCATIONS,
    { ...formData },
    LOCATION_ROOT_EXTRAS,
  )

  return resourceOk({
    locations: [
      locationInputOf(
        existing,
        projectId,
        recordId,
        rootFields,
        dataFields,
        templateId,
      ),
    ],
  })
}

export const locationResourceType: ProjectResourceType<
  LocationResource,
  LocationResourceDomainFailure
> = {
  typeId: ResourceTypeId.LOCATIONS,
  saveDocument: SaveLocationsDocument,
  decodeResources: decodeLocationResources,
  toRecord: toLocationResourceRecord,
  buildSaveVariables: buildLocationSaveVariables,
}
